import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import sharedDataApi from "../../api/sharedDataApi";

// Async thunks for user operations
export const fetchUsers = createAsyncThunk(
  'userManagement/fetchUsers',
  async (_, { rejectWithValue }) => {
    try {
      let allUsers = [];
      let nextToken = null;

      do {
        const response = await sharedDataApi.listSdpUsers({
          nextToken: nextToken,
          limit: 100
        });
        allUsers = [...allUsers, ...response.items];
        nextToken = response.nextToken;
      } while (nextToken);

      return allUsers;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch users');
    }
  }
);

export const searchUsers = createAsyncThunk(
  'userManagement/searchUsers',
  async (searchParams, { getState }) => {
    const { userManagement } = getState();
    const { allUsers } = userManagement;
    const { searchText, filters } = searchParams;

    // Helper function to parse role from JSON string
    const parseRole = (roleString) => {
      if (!roleString) return "Default Access";
      try {
        const parsed = JSON.parse(roleString);
        if (Array.isArray(parsed)) {
          return parsed[0];
        }
      } catch (e) {}
      return roleString;
    };

    let filteredUsers = [...allUsers];

    // Apply search filter
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filteredUsers = filteredUsers.filter(user => 
        user.Name.toLowerCase().includes(searchLower) ||
        user.Email.toLowerCase().includes(searchLower) ||
        parseRole(user.Role).toLowerCase().includes(searchLower)
      );
    }

    // Apply additional filters if any
    if (filters) {
      if (filters.role && filters.role !== 'all') {
        filteredUsers = filteredUsers.filter(user => parseRole(user.Role) === filters.role);
      }
    }

    return filteredUsers;
  }
);

const initialState = {
  // Data
  allUsers: [],
  filteredUsers: [],
  currentPageUsers: [],
  
  // UI State
  isLoading: false,
  searchText: '',
  filters: {
    role: 'all',
  },
  
  // Pagination
  pagination: {
    currentPage: 1,
    itemsPerPage: 10,
    totalItems: 0,
    totalPages: 0
  },
  
  // Sorting
  sorting: {
    field: 'Name',
    direction: 'asc'
  },
  
  // Error handling
  error: null,
  
  // Statistics
  stats: {
    totalUsers: 0,
    adminUsers: 0,
    regularUsers: 0,
    recentlyAdded: 0
  }
};

const userManagementSlice = createSlice({
  name: 'userManagement',
  initialState,
  reducers: {
    // UI State Management
    setSearchText: (state, action) => {
      state.searchText = action.payload;
    },
    
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    
    setSorting: (state, action) => {
      const { field } = action.payload;
      if (state.sorting.field === field) {
        state.sorting.direction = state.sorting.direction === 'asc' ? 'desc' : 'asc';
      } else {
        state.sorting.field = field;
        state.sorting.direction = 'asc';
      }
    },
    
    // Pagination
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    
    updateCurrentPage: (state, action) => {
      const { currentPage } = action.payload;
      state.pagination.currentPage = currentPage;
      
      const startIndex = (currentPage - 1) * state.pagination.itemsPerPage;
      const endIndex = startIndex + state.pagination.itemsPerPage;
      
      // Apply sorting before pagination
      const sortedUsers = [...state.filteredUsers].sort((a, b) => {
        const { field, direction } = state.sorting;
        if (!field) return 0;
        
        let aValue = a[field] || '';
        let bValue = b[field] || '';
        
        // Handle role parsing for sorting
        if (field === 'Role') {
          aValue = aValue || "Default Access";
          bValue = bValue || "Default Access";
        }
        
        // Handle date sorting
        if (field === 'createdAt') {
          aValue = new Date(aValue || 0);
          bValue = new Date(bValue || 0);
        }
        
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          const comparison = aValue.toLowerCase().localeCompare(bValue.toLowerCase());
          return direction === 'asc' ? comparison : -comparison;
        }
        
        if (aValue < bValue) return direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return direction === 'asc' ? 1 : -1;
        return 0;
      });
      
      state.currentPageUsers = sortedUsers.slice(startIndex, endIndex);
    },
    
    // Update items per page
    updateItemsPerPage: (state, action) => {
      state.pagination.itemsPerPage = action.payload.itemsPerPage;
      state.pagination.currentPage = 1; // Reset to first page when changing items per page
      
      // Recalculate total pages
      state.pagination.totalPages = Math.ceil(state.pagination.totalItems / state.pagination.itemsPerPage);
      
      // Update current page users
      const startIndex = 0; // Start from beginning since we reset to page 1
      const endIndex = state.pagination.itemsPerPage;
      
      // Apply sorting before pagination
      const sortedUsers = [...state.filteredUsers].sort((a, b) => {
        const { field, direction } = state.sorting;
        if (!field) return 0;
        
        let aValue = a[field] || '';
        let bValue = b[field] || '';
        
        // Handle role parsing for sorting
        if (field === 'Role') {

          aValue = aValue || "Default Access";
          bValue = bValue || "Default Access";
        }
        
        // Handle date sorting
        if (field === 'createdAt') {
          aValue = new Date(aValue || 0);
          bValue = new Date(bValue || 0);
        }
        
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          const comparison = aValue.toLowerCase().localeCompare(bValue.toLowerCase());
          return direction === 'asc' ? comparison : -comparison;
        }
        
        if (aValue < bValue) return direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return direction === 'asc' ? 1 : -1;
        return 0;
      });
      
      state.currentPageUsers = sortedUsers.slice(startIndex, endIndex);
    },
    
    // Error Management
    clearError: (state) => {
      state.error = null;
    },
    
    setError: (state, action) => {
      state.error = action.payload;
    },
    
    // Calculate statistics
    calculateStats: (state) => {
      const users = state.allUsers;
      
      // Helper function to parse role from JSON string
      const parseRole = (roleString) => {
        if (!roleString) return "Default Access";
        try {
          const parsed = JSON.parse(roleString);
          if (Array.isArray(parsed)) {
            return parsed[0];
          }
        } catch (e) {}
        return roleString;
      };
      
      state.stats.totalUsers = users.length;
      state.stats.adminUsers = users.filter(user => parseRole(user.Role) === 'admin').length;
      state.stats.regularUsers = users.filter(user => parseRole(user.Role) === 'Default Access').length;
      
      // Calculate recently added (last 30 days)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      state.stats.recentlyAdded = users.filter(user => 
        user.createdAt && new Date(user.createdAt) > thirtyDaysAgo
      ).length;
    }
  },
  
  extraReducers: (builder) => {
    builder
      // Fetch Users
      .addCase(fetchUsers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.lastOperation = 'fetch';
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.allUsers = action.payload;
        state.filteredUsers = action.payload;
        state.pagination.totalItems = action.payload.length;
        state.pagination.totalPages = Math.ceil(action.payload.length / state.pagination.itemsPerPage);
        userManagementSlice.caseReducers.calculateStats(state);
        userManagementSlice.caseReducers.updateCurrentPage(state, { 
          payload: { currentPage: 1 } 
        });
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Search Users
      .addCase(searchUsers.fulfilled, (state, action) => {
        state.filteredUsers = action.payload;
        state.pagination.totalItems = action.payload.length;
        state.pagination.totalPages = Math.ceil(action.payload.length / state.pagination.itemsPerPage);
        state.pagination.currentPage = 1;
        userManagementSlice.caseReducers.updateCurrentPage(state, { 
          payload: { currentPage: 1 } 
        });
      });
  }
});

// Action creators
export const {
  setSearchText,
  setFilters,
  setSorting,
  setPagination,
  updateCurrentPage,
  updateItemsPerPage,
  clearError,
  setError,
  calculateStats
} = userManagementSlice.actions;

// Selectors
export const selectUserManagement = (state) => state.userManagement;
export const selectUsers = (state) => state.userManagement.currentPageUsers;
export const selectAllUsers = (state) => state.userManagement.allUsers;
export const selectUserStats = (state) => state.userManagement.stats;
export const selectUserManagementLoading = (state) => state.userManagement.isLoading;
export const selectUserManagementError = (state) => state.userManagement.error;
export const selectPagination = (state) => state.userManagement.pagination;
export const selectSorting = (state) => state.userManagement.sorting;
export const selectSearchText = (state) => state.userManagement.searchText;
export const selectFilters = (state) => state.userManagement.filters;

export default userManagementSlice.reducer;
